<template>
  <PortalLayout section="Client Metrics" hideHeader :IsClientSelectorOnly="true">
    <h1 class="py-3 text-xl border-b flex">
      <div class="flex-grow">
        <div class="font-bold">Client Metrics</div>
        <div>
          {{ portal?.clientName }}
          <span v-if="!portal?.isActive" class="font-bold text-sm">(disabled)</span>
        </div>
      </div>
    </h1>
    <Loading v-if="isMetricsLoading" />
    <div v-else>
      <div v-if="isOnQAOrLocal()" class="text-red-700 cursor-pointer inline">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-8 h-8 inline"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
          />
        </svg>
        You are on Dev or QA, but metric data is recorded from Production. Data and users on this
        page may be incomplete or inaccurate.
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div id="pageViewsChart"></div>
        <div id="rageClicksChart"></div>
        <div id="sessionsChart"></div>
        <div id="uniqueUsersChart"></div>
        <div v-if="portal.isRespondEnabled" id="respondChart"></div>
        <div v-if="portal.isTrainingEnabled" id="videosChart"></div>
        <div v-if="portal.isTrainingEnabled" id="questionsChart"></div>
        <div v-if="portal.isTrainingEnabled" id="testsChart"></div>
      </div>

      <BaseSelect
        class="text-sm"
        :options="timeFrameOptions"
        :defaultOption="null"
        defaultText="Time Frame"
        v-model="selectedTimeFrame"
      />

      <div
        class="flex"
        v-if="metricResults != null && previousPeriodResults != null && metricsDifference != null"
      >
        <div class="basis-1/3 text-center">
          <div>Unique users</div>
          <div
            :class="
              metricsDifference.uniqueUsers != 0
                ? metricsDifference.uniqueUsers > 0
                  ? 'text-green-500'
                  : 'text-red-500'
                : ''
            "
          >
            {{ metricResults.uniqueUsers }} ({{
              (metricsDifference.uniqueUsers > -1 ? "+" : "") + metricsDifference.uniqueUsers
            }})
          </div>
        </div>
        <div class="basis-1/3 text-center">
          <div>Sessions</div>
          <div
            :class="
              metricsDifference.sessions != 0
                ? metricsDifference.sessions > 0
                  ? 'text-green-500'
                  : 'text-red-500'
                : ''
            "
          >
            {{ metricResults.sessions }} ({{
              (metricsDifference.sessions > -1 ? "+" : "") + metricsDifference.sessions
            }})
          </div>
        </div>
        <div class="basis-1/3 text-center">
          <div>Page Views</div>
          <div
            :class="
              metricsDifference.pageViews != 0
                ? metricsDifference.pageViews > 0
                  ? 'text-green-500'
                  : 'text-red-500'
                : ''
            "
          >
            {{ metricResults.pageViews }} ({{
              (metricsDifference.pageViews > -1 ? "+" : "") + metricsDifference.pageViews
            }})
          </div>
        </div>
        <div class="basis-1/3 text-center">
          <div>Rage Clicks</div>
          <div
            :class="
              metricsDifference.rageClicks != 0
                ? metricsDifference.rageClicks < 0
                  ? 'text-green-500'
                  : 'text-red-500'
                : ''
            "
          >
            {{ metricResults.rageClicks }} ({{
              (metricsDifference.rageClicks > -1 ? "+" : "") + metricsDifference.rageClicks
            }})
          </div>
        </div>
        <div v-if="portal.isRespondEnabled" class="basis-1/3 text-center">
          <div>Respond Views</div>
          <div
            :class="
              metricsDifference.respondViews != 0
                ? metricsDifference.respondViews > 0
                  ? 'text-green-500'
                  : 'text-red-500'
                : ''
            "
          >
            {{ metricResults.respondViews }} ({{
              (metricsDifference.respondViews > -1 ? "+" : "") + metricsDifference.respondViews
            }})
          </div>
        </div>
        <div v-if="portal.isTrainingEnabled" class="basis-1/3 text-center">
          <div>Videos Views</div>
          <div
            :class="
              metricsDifference.videosEnded != 0
                ? metricsDifference.videosEnded > 0
                  ? 'text-green-500'
                  : 'text-red-500'
                : ''
            "
          >
            {{ metricResults.videosEnded }} ({{
              (metricsDifference.videosEnded > -1 ? "+" : "") + metricsDifference.videosEnded
            }})
          </div>
        </div>
        <div v-if="portal.isTrainingEnabled" class="basis-1/3 text-center">
          <div>Questions Answered</div>
          <div
            :class="
              metricsDifference.questionsAnswered != 0
                ? metricsDifference.questionsAnswered > 0
                  ? 'text-green-500'
                  : 'text-red-500'
                : ''
            "
          >
            {{ metricResults.questionsAnswered }} ({{
              (metricsDifference.questionsAnswered > -1 ? "+" : "") +
              metricsDifference.questionsAnswered
            }})
          </div>
        </div>
        <div v-if="portal.isTrainingEnabled" class="basis-1/3 text-center">
          <div>Tests Completed</div>
          <div
            :class="
              metricsDifference.testsCompleted != 0
                ? metricsDifference.testsCompleted > 0
                  ? 'text-green-500'
                  : 'text-red-500'
                : ''
            "
          >
            {{ metricResults.testsCompleted }} ({{
              (metricsDifference.testsCompleted > -1 ? "+" : "") + metricsDifference.testsCompleted
            }})
          </div>
        </div>
      </div>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import {
  getClientMetricGraphData,
  getClientMetrics,
  GraphData,
  MetricsReturnData,
} from "../../helpers/metrics";
import { useBldgStore } from "../../store/bldgStore";
import { isOnQAOrLocal, timeFrameOptions } from "../../helpers";
import ApexCharts from "apexcharts";

const bldgStore = useBldgStore();

const isMetricsLoading = ref(false);

const selectedTimeFrame: Ref<number | null> = ref(null);
const metricResults = ref(null as MetricsReturnData);
const previousPeriodResults = ref(null as MetricsReturnData);
const metricsDifference = computed(() => {
  if (metricResults.value != null && previousPeriodResults.value != null)
    return {
      uniqueUsers: metricResults.value.uniqueUsers - previousPeriodResults.value.uniqueUsers,
      pageViews: metricResults.value.pageViews - previousPeriodResults.value.pageViews,
      sessions: metricResults.value.sessions - previousPeriodResults.value.sessions,
      rageClicks: metricResults.value.rageClicks - previousPeriodResults.value.rageClicks,
      respondViews: metricResults.value.respondViews - previousPeriodResults.value.respondViews,
      videosEnded: metricResults.value.videosEnded - previousPeriodResults.value.videosEnded,
      questionsAnswered:
        metricResults.value.questionsAnswered - previousPeriodResults.value.questionsAnswered,
      testsCompleted:
        metricResults.value.testsCompleted - previousPeriodResults.value.testsCompleted,
    } as MetricsReturnData;

  return null;
});

var portal = computed(() => {
  let p = bldgStore.property;
  if (p !== null && p.siteCode) {
    return p;
  }
});

onMounted(async () => await loadClientData());
watch(portal, loadClientData);
watch(selectedTimeFrame, getMetricData);

async function loadClientData() {
  if (portal?.value?.clientId) {
    getGraphData();
    getMetricData();
  }
}

async function getMetricData() {
  if (portal?.value?.clientId && selectedTimeFrame.value != null) {
    isMetricsLoading.value = true;

    let currentPeriodFromDate = new Date(Date.now());
    currentPeriodFromDate.setDate(currentPeriodFromDate.getDate() - selectedTimeFrame.value);

    let previousPeriodFromDate = new Date(Date.now());
    previousPeriodFromDate.setDate(
      previousPeriodFromDate.getDate() - (selectedTimeFrame.value + selectedTimeFrame.value)
    );

    await Promise.all([
      getClientMetrics(portal.value.clientId, currentPeriodFromDate, new Date(Date.now())),
      getClientMetrics(portal.value.clientId, previousPeriodFromDate, currentPeriodFromDate),
    ]).then((allResults) => {
      metricResults.value = allResults[0];
      previousPeriodResults.value = allResults[1];
      isMetricsLoading.value = false;
    });
  }
}

async function getGraphData() {
  let graphData = await getClientMetricGraphData(portal.value.clientId);

  var sessionsChart = new ApexCharts(
    document.querySelector("#sessionsChart"),
    createChartOptions("Sessions", graphData.sessions)
  );
  var uniqueUsersChart = new ApexCharts(
    document.querySelector("#uniqueUsersChart"),
    createChartOptions("Unique Users", graphData.uniqueUsers)
  );
  var pageViewsChart = new ApexCharts(
    document.querySelector("#pageViewsChart"),
    createChartOptions("Page Views", graphData.pageViews)
  );
  var rageClicksChart = new ApexCharts(
    document.querySelector("#rageClicksChart"),
    createChartOptions("Rage Clicks", graphData.rageClicks)
  );
  var respondChart = new ApexCharts(
    document.querySelector("#respondChart"),
    createChartOptions("Respond", graphData.respondViews)
  );
  var videosChart = new ApexCharts(
    document.querySelector("#videosChart"),
    createChartOptions("Videos Watched", graphData.videosEnded)
  );
  var questionsChart = new ApexCharts(
    document.querySelector("#questionsChart"),
    createChartOptions("Questions Answered", graphData.questionsAnswered)
  );
  var testsChart = new ApexCharts(
    document.querySelector("#testsChart"),
    createChartOptions("Tests Completed", graphData.testsCompleted)
  );

  sessionsChart.render();
  uniqueUsersChart.render();
  pageViewsChart.render();
  rageClicksChart.render();
  respondChart.render();
  videosChart.render();
  questionsChart.render();
  testsChart.render();
}

function createChartOptions(name: string, data: GraphData) {
  return {
    title: {
      text: name,
      align: "left",
    },
    chart: {
      type: "area",
    },
    series: [
      {
        name: name,
        data: data.dates.map((date, i) => {
          return { x: date, y: data.dataSeries[0][i] };
        }),
      },
    ],
    markers: {
      size: 0,
    },
    xaxis: {
      labels: {
        formatter: function (value: Date) {
          return new Date(value).toDateString(); // The formatter function overrides format property
        },
      },
    },
  };
}
</script>
